import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { FieldArray, Form, Formik, FormikErrors, FormikValues } from 'formik';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import * as Yup from 'yup';
import { ERROR_CODES } from '../../../types/error';
import { IEntitlement } from '../../../types/subscribed-access';

import { Add } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Identity } from '../../../types/identity';
import CustomDatePicker from '../../General/CustomDatePicker';
import { ClaimError, ClaimField } from '../ClaimsTable/ClaimField';

import {
  getEarliestValidFromDateInClaims,
  getLatestValidToDateFromClaims,
} from '../../../util/subscribedAccessUtils';
import useBrandGroupEntitlements from '../../../hooks/useBrandGroupEntitlements';
import { useAuth } from '../../../providers/AuthProvider';
import { Entitlement, EntitlementProcess } from '../../../types/entitlements';
import { entitlementsService } from '../../../services/entitlementService';
import { EntitlementDialogData } from '../ClaimsTable/entitlements-ui-types';

export enum DIALOG_TYPE {
  ADD = 'ADD',
  EDIT = 'EDIT',
}

type EntitlementDialogProps = {
  open: boolean;
  type: DIALOG_TYPE;
  identityId: Identity['id'];
  onSave: () => void;
  onClose: () => void;
  data?: Entitlement;
};

export const EntitlementDialog: FC<EntitlementDialogProps> = ({
  open,
  type,
  identityId,
  onClose,
  onSave,
  data,
}) => {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const { customerSupportMail } = useAuth();
  const { isKnownBrand, isKnownBrandCode } = useBrandGroupEntitlements();

  const [addSourceId, setAddSourceId] = useState(() => crypto.randomUUID());

  useEffect(() => {
    if (open && !data) {
      setAddSourceId(crypto.randomUUID());
    }
  }, [open, data]);

  const MIN_VALID_FROM = '2000-01-01T00:00:00.000Z';

  const normalizeValidFrom = (validFrom: string | undefined): string | undefined => {
    if (!validFrom) return validFrom;
    return dayjs(validFrom).isBefore(dayjs(MIN_VALID_FROM)) ? MIN_VALID_FROM : validFrom;
  };

  const initialValues: EntitlementDialogData = useMemo(() => {
    if (data) {
      const normalized = {
        ...data,
        claims: (data.claims ?? []).map((c) => ({
          ...c,
          validFrom: normalizeValidFrom(c.validFrom),
        })),
      };
      const earliestDate = getEarliestValidFromDateInClaims(normalized);
      const latestDate = getLatestValidToDateFromClaims(normalized);
      return {
        ...normalized,
        defaultValidFrom: earliestDate ?? undefined,
        defaultValidTo: latestDate ?? undefined,
      };
    }
    return {
      id: {
        process: EntitlementProcess.MANUAL,
        system: EntitlementProcess.MANUAL,
        sourceId: addSourceId,
      },
      claims: [
        {
          brand: '',
          code: '',
          isActive: true,
        },
      ],
      importedBy: customerSupportMail,
    };
  }, [customerSupportMail, data, addSourceId]);

  const validationSchema = useMemo(() => {
    return Yup.object()
      .shape({
        defaultValidFrom: Yup.date().nullable(),
        defaultValidTo: Yup.date()
          .nullable()
          .min(
            Yup.ref('defaultValidFrom'),
            t('entitlements.validation.validToBeforeValidFrom') ?? undefined
          ),
        claims: Yup.array()
          .of(
            Yup.object().shape({
              brand: Yup.string()
                .min(2)
                .required(t('entitlements.validation.requiredBrand') ?? 'Invalid field'),
              code: Yup.string()
                .min(2)
                .required(t('entitlements.validation.requiredBrandCode') ?? 'Invalid field'),
              validFrom: Yup.date().nullable(),
              validTo: Yup.date().nullable(),
            })
          )
          .min(1, 'At least one claim should be present')
          .required('Required'),
      })
      .test((values) => {
        const claims = values.claims ?? [];
        const validationErrors: Yup.ValidationError[] = [];
        claims.forEach(({ validFrom, validTo, brand, code }, index) => {
          const formikId = `claims.${index}`;
          const hasValidFromDate = !!validFrom;
          const hasValidToDate = !!validTo;
          const hasValidBrand = brand && isKnownBrand(brand);
          const hasValidBrandCode = code && isKnownBrandCode(brand, code);
          if (hasValidFromDate && !hasValidToDate) {
            validationErrors.push(
              new Yup.ValidationError(
                'A valid to date is required',
                undefined,
                `${formikId}.validTo`
              )
            );
          }
          if (hasValidToDate && !hasValidFromDate) {
            validationErrors.push(
              new Yup.ValidationError(
                'A valid from date is required',
                undefined,
                `${formikId}.validFrom`
              )
            );
          }
          if (dayjs(validTo).isBefore(dayjs(validFrom))) {
            validationErrors.push(
              new Yup.ValidationError(
                'A valid from date must be after the valid to date',
                undefined,
                `${formikId}.validTo`
              )
            );
          }
          if (!hasValidBrand) {
            validationErrors.push(
              new Yup.ValidationError(
                'A valid brand needs to be selected',
                undefined,
                `${formikId}.brand`
              )
            );
          }
          if (!hasValidBrandCode) {
            validationErrors.push(
              new Yup.ValidationError(
                'A valid code needs to be selected',
                undefined,
                `${formikId}.code`
              )
            );
          }
        });
        return new Yup.ValidationError(validationErrors);
      });
  }, [isKnownBrand, isKnownBrandCode, t]);

  const handleUpdate = useCallback(
    async (values: EntitlementDialogData) => {
      try {
        const mergedEntitlementData = { ...data, ...values };
        await entitlementsService.putEntitlement(identityId, mergedEntitlementData.id, {
          ...mergedEntitlementData,
          modificationTimeStamp: new Date().toISOString(),
        });
      } catch (e) {
        console.error('Error while adding/editing subscribed access: ', e);
        handleError(Error(ERROR_CODES.REACT_ERROR));
      } finally {
        onSave();
      }
    },
    [data, handleError, identityId, onSave]
  );

  const getEntitlementError = useCallback(
    (errors: FormikErrors<FormikValues>, index: number): ClaimError | undefined => {
      if (errors.claims && Array.isArray(errors.claims) && errors.claims?.length >= index - 1) {
        return errors?.claims[index] as unknown as ClaimError;
      }
    },
    []
  );

  const headerTitle = useMemo(() => {
    switch (type) {
      case DIALOG_TYPE.ADD:
        return t('entitlements.addEntitlement');
      case DIALOG_TYPE.EDIT:
        return t('entitlements.editEntitlement');
    }
  }, [t, type]);

  const saveLabel = useMemo(() => {
    switch (type) {
      case DIALOG_TYPE.ADD:
        return t('actions.add');
      case DIALOG_TYPE.EDIT:
        return t('actions.submit');
    }
  }, [t, type]);

  const onChangeDefaultDate = useCallback(
    (
      dateField: 'validFrom' | 'validTo',
      date: Date | undefined,
      currentValues: EntitlementDialogData,
      setValues: (values: EntitlementDialogData, shouldValidate: boolean) => void
    ) => {
      const defaultField = dateField === 'validFrom' ? 'defaultValidFrom' : 'defaultValidTo';
      const currentDefaultDate = currentValues[defaultField];

      const newDefaultDate = date?.toISOString();

      const updatedClaims = currentValues.claims.map((claim) => {
        const currentDate = claim[dateField];
        const shouldChangeDate = !currentDate || currentDate === currentDefaultDate;
        return { ...claim, [dateField]: shouldChangeDate ? newDefaultDate : currentDate };
      });

      setValues({ ...currentValues, [defaultField]: newDefaultDate, claims: updatedClaims }, true);
    },
    []
  );

  return (
    <Dialog open={open} maxWidth={'lg'}>
      <DialogContent>
        <Formik
          validateOnMount
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => handleUpdate(values)}>
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleChange,
            handleBlur,
            handleSubmit,
            setValues,
            setFieldValue,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Typography
                variant="MHHeading"
                sx={{
                  mb: 2,
                }}>
                {headerTitle}
              </Typography>
              <Grid
                container
                sx={{
                  mb: 5,
                  gap: 1,
                }}>
                <Grid item>
                  <CustomDatePicker
                    label={'Default valid from'}
                    time={{ hour: 0, minute: 0, second: 0 }}
                    value={values.defaultValidFrom}
                    error={errors.defaultValidFrom}
                    onChange={(date) => onChangeDefaultDate('validFrom', date, values, setValues)}
                  />
                </Grid>
                <Grid item>
                  <CustomDatePicker
                    label={'Default valid to'}
                    time={{ hour: 23, minute: 59, second: 59 }}
                    value={values.defaultValidTo}
                    error={errors.defaultValidTo}
                    onChange={(date) => onChangeDefaultDate('validTo', date, values, setValues)}
                  />
                </Grid>
              </Grid>
              <Typography
                variant="MHSubHeading"
                sx={{
                  mb: 2,
                }}>
                {t('entitlements.claims')}
              </Typography>
              <Grid
                container
                direction="row"
                sx={{
                  mb: 5,
                  rowGap: 1,
                }}>
                <FieldArray
                  name="claims"
                  render={(arrayHelper) => (
                    <>
                      {values.claims?.map((element, index) => (
                        <ClaimField
                          key={index}
                          formikId={`claims.${index}`}
                          error={getEntitlementError(errors, index)}
                          values={element}
                          setFieldValue={setFieldValue}
                          onRemove={
                            (values.claims?.length ?? 0) > 1
                              ? () => arrayHelper.remove(index)
                              : undefined
                          }
                        />
                      ))}
                      <Grid
                        item
                        xs={12}
                        sx={{
                          flexDirection: 'row',
                          ml: 1,
                          mt: 1,
                        }}>
                        <Button
                          variant="text"
                          type="button"
                          color="info"
                          startIcon={<Add />}
                          onBlur={handleBlur}
                          onClick={() => {
                            const ent: IEntitlement = {
                              brandCode: '',
                              code: '',
                              isActive: true,
                              validFrom: values.defaultValidFrom,
                              validTo: values.defaultValidTo,
                            };
                            arrayHelper.push(ent);
                          }}>
                          {t('entitlements.addClaim')}
                        </Button>
                        {!Array.isArray(errors?.claims) && (
                          <InputLabel sx={{ color: '#d32f2f', padding: '0.5rem 0 0.5rem 0' }}>
                            {errors?.claims}
                          </InputLabel>
                        )}
                      </Grid>
                    </>
                  )}
                />
              </Grid>
              <Grid
                container
                direction="row"
                sx={{
                  mb: 5,
                  rowGap: 1,
                }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    label="Imported by"
                    id="importedBy"
                    value={customerSupportMail}
                    onBlur={handleBlur}
                    disabled={true}
                    inputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12}></Grid>
              </Grid>
              <Grid
                container
                direction="row"
                sx={{
                  justifyContent: 'end',
                }}>
                <Grid item>
                  <Button
                    variant="brandContained"
                    color="brandGrey"
                    onClick={onClose}
                    disabled={isSubmitting}
                    sx={{ mr: 1 }}>
                    {t('actions.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="brandContained"
                    color="brandBlack"
                    startIcon={isSubmitting ? <CircularProgress size="1em" /> : undefined}
                    disabled={isSubmitting || !touched || !!Object.keys(errors).length}>
                    {saveLabel}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
